import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert, Lock, CreditCard, FileText, CheckCircle2, ExternalLink } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError } from "@/lib/api";
import { useCart } from "@/hooks/useCart";
import { useCreateCheckoutSession, useCreateInvoiceCheckout, usePaymentMethods } from "@/hooks/usePayments";

/**
 * Checkout entry. The customer picks how to pay:
 *   • Card — Stripe hosted Checkout (charged now), then redirect.
 *   • Invoice — Stripe emails an invoice payable within 14 days (card or bank);
 *     the service activates once the invoice is paid.
 */
export function CheckoutPage() {
  const navigate = useNavigate();
  const { data: cart, isLoading } = useCart();
  const createSession = useCreateCheckoutSession();
  const createInvoice = useCreateInvoiceCheckout();

  const { data: cards } = usePaymentMethods();
  const savedCards = cards ?? [];

  const [error, setError] = React.useState<string | null>(null);
  const [invoiceUrl, setInvoiceUrl] = React.useState<string | null>(null);
  const [invoiceSent, setInvoiceSent] = React.useState(false);
  const [selectedCard, setSelectedCard] = React.useState<string | null>(null);

  // Pre-select the default card (or the first) once cards load.
  React.useEffect(() => {
    if (savedCards.length > 0 && !selectedCard) {
      const def = savedCards.find((c) => c.isDefault) ?? savedCards[0];
      setSelectedCard(def.id);
    }
  }, [savedCards, selectedCard]);

  const busy = createSession.isPending || createInvoice.isPending;
  const empty = !isLoading && (!cart || cart.items.length === 0);

  async function payByCard(paymentMethodId?: string) {
    setError(null);
    try {
      const res = await createSession.mutateAsync(paymentMethodId);
      if (res.checkout_url) {
        window.location.href = res.checkout_url;      // hosted Checkout (no saved card)
      } else if (res.paid) {
        navigate("/checkout/success");                // one-click saved-card purchase — already done
      } else {
        setError("Couldn't start checkout. Please try again.");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't start checkout. Please try again.");
    }
  }

  async function payByInvoice() {
    setError(null);
    try {
      const res = await createInvoice.mutateAsync();
      setInvoiceUrl(res.hosted_invoice_url);
      setInvoiceSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create the invoice. Please try again.");
    }
  }

  return (
    <div className="mx-auto max-w-md py-12 sm:py-16">
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          {empty ? (
            <>
              <IconCircle tone="muted"><ShieldAlert className="h-6 w-6" /></IconCircle>
              <p className="font-medium">Your cart is empty.</p>
              <Button asChild><Link to="/plans">Browse plans</Link></Button>
            </>
          ) : invoiceSent ? (
            <>
              <IconCircle tone="success"><CheckCircle2 className="h-6 w-6" /></IconCircle>
              <p className="font-medium">Invoice on its way</p>
              <p className="text-sm text-muted-foreground">
                We’ve emailed your invoice. You can pay it by card or bank transfer within 14 days —
                your services activate as soon as it’s paid.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {invoiceUrl && (
                  <Button asChild>
                    <a href={invoiceUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" /> View and pay invoice
                    </a>
                  </Button>
                )}
                <Button variant="outline" asChild><Link to="/billing">Go to billing</Link></Button>
              </div>
            </>
          ) : busy ? (
            <>
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
              <p className="font-medium">
                {createSession.isPending ? "Processing your payment…" : "Preparing your invoice…"}
              </p>
            </>
          ) : error ? (
            <>
              <IconCircle tone="destructive"><ShieldAlert className="h-6 w-6" /></IconCircle>
              <p className="font-medium">{error}</p>
              <div className="flex gap-2">
                <Button variant="outline" asChild><Link to="/cart">Back to cart</Link></Button>
                <Button onClick={() => setError(null)}>Try again</Button>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-display text-xl font-bold">How would you like to pay?</h1>
              <p className="text-sm text-muted-foreground">Choose a payment method to complete your order.</p>

              {savedCards.length > 0 ? (
                <div className="mt-2 flex w-full flex-col gap-3">
                  <div className="space-y-2 text-left">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pay with</p>
                    {savedCards.map((c) => {
                      const active = selectedCard === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setSelectedCard(c.id)}
                          className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-sm transition ${
                            active ? "border-primary ring-1 ring-primary" : "border-border hover:border-primary/50"
                          }`}
                        >
                          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <CreditCard className="h-4 w-4 text-muted-foreground" />
                            <span className="capitalize font-medium">{c.brand}</span>
                            <span className="text-muted-foreground">•••• {c.last4}</span>
                            <span className="text-xs text-muted-foreground">
                              exp {String(c.expMonth).padStart(2, "0")}/{String(c.expYear).slice(-2)}
                            </span>
                            {c.isDefault && <Badge variant="success">Default</Badge>}
                          </span>
                          {active && <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />}
                        </button>
                      );
                    })}
                  </div>

                  <Button size="lg" className="w-full" disabled={!selectedCard} onClick={() => payByCard(selectedCard ?? undefined)}>
                    <CreditCard className="h-4 w-4" /> Pay with this card
                  </Button>
                  <Button size="lg" variant="ghost" className="w-full" onClick={() => payByCard(undefined)}>
                    Use a different card
                  </Button>
                  <Button size="lg" variant="outline" className="w-full" onClick={payByInvoice}>
                    <FileText className="h-4 w-4" /> Pay by invoice (bank transfer / 14 days)
                  </Button>
                </div>
              ) : (
                <div className="mt-2 flex w-full flex-col gap-3">
                  <Button size="lg" className="w-full" onClick={() => payByCard(undefined)}>
                    <CreditCard className="h-4 w-4" /> Pay by card
                  </Button>
                  <Button size="lg" variant="outline" className="w-full" onClick={payByInvoice}>
                    <FileText className="h-4 w-4" /> Pay by invoice (bank transfer / 14 days)
                  </Button>
                </div>
              )}

              <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Lock className="h-3.5 w-3.5" /> Payments processed securely by Stripe
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function IconCircle({ tone, children }: { tone: "muted" | "success" | "destructive"; children: React.ReactNode }) {
  const cls =
    tone === "success"
      ? "bg-brand-green/10 text-brand-green"
      : tone === "destructive"
        ? "bg-destructive/10 text-destructive"
        : "bg-muted text-muted-foreground";
  return <div className={`flex h-12 w-12 items-center justify-center rounded-full ${cls}`}>{children}</div>;
}
